import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../api/api';
import { ShopContext } from '../context/ShopContext';
import { 
  Avatar,
  Box, 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  CircularProgress, 
  Alert, 
  Divider, 
  Chip,
  Pagination
} from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ShoppingBagOutlined from '@mui/icons-material/ShoppingBagOutlined';
import LocalShippingOutlined from '@mui/icons-material/LocalShippingOutlined';
import CheckCircleOutlineOutlined from '@mui/icons-material/CheckCircleOutlineOutlined';
import PendingOutlined from '@mui/icons-material/PendingOutlined';

const getStatusIcon = (status) => {
  switch (status?.toLowerCase()) {
    case 'delivered':
      return <CheckCircleOutlineOutlined color="success" />;
    case 'shipped':
      return <LocalShippingOutlined color="primary" />;
    case 'processing':
      return <PendingOutlined color="warning" />;
    default:
      return <CheckCircleIcon color="disabled" />;
  }
};

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'delivered':
      return 'success';
    case 'shipped':
      return 'info';
    case 'processing':
      return 'warning';
    case 'cancelled':
      return 'error';
    default:
      return 'default';
  }
};

const Orders = () => {
  const { currency } = useContext(ShopContext);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages] = useState(1);
  const navigate = useNavigate();

  const fetchOrders = useCallback(async (pageNum = 1) => {    
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login', { state: { from: '/orders' } });
        return;
      }
      
      const response = await api.get(`/api/orders?page=${pageNum}&limit=10`);
      
      if (response.data?.success) {
        const orders = response.data.orders || [];
        setOrders(orders);
        
        if (orders.length === 0) {
          setError('No orders found. Your order history is empty.');
        }
      } else {
        const errorMsg = response.data?.message || 'Failed to load orders. Please try again.';
        setError(errorMsg);
      }
    } catch (err) {
      let errorMessage = 'Failed to load orders. ';
      
      if (err.response) {
        if (err.response.status === 401) {
          navigate('/login', { state: { from: '/orders' } });
          return;
        }
        errorMessage += err.response.data?.message || `Server responded with status ${err.response.status}`;
      } else if (err.request) {
        errorMessage += 'No response from server. Please check your connection.';
      } else {
        errorMessage += err.message || 'Unknown error occurred';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // Wrap fetchOrders in useCallback to prevent infinite re-renders
  const fetchOrdersCallback = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login', { state: { from: '/orders' } });
      return;
    }
    fetchOrders(page);
  }, [page, navigate, fetchOrders]);

  useEffect(() => {
    // Only fetch orders if we have a token
    fetchOrdersCallback();
    
    // Cleanup function to prevent state updates after unmount
    return () => {
      // Any cleanup if needed
    };
  }, [navigate, fetchOrdersCallback]);

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleRetry = () => {
    fetchOrders(page);
  };

  if (loading && orders.length === 0) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        minHeight: '50vh',
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress />
        <Typography variant="body1" color="text.secondary">
          Loading your orders...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert 
        severity="error" 
        sx={{ m: 3 }}
        action={
          <Button color="inherit" size="small" onClick={handleRetry}>
            Retry
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
        My Orders
      </Typography>
      
      {orders.length === 0 ? (
        <Box 
          sx={{ 
            textAlign: 'center', 
            py: 8,
            border: '1px dashed', 
            borderColor: 'divider',
            borderRadius: 1,
            mt: 3
          }}
        >
          <ShoppingBagOutlined 
            sx={{ 
              fontSize: 64, 
              color: 'text.secondary', 
              mb: 2 
            }} 
          />
          <Typography variant="h6" gutterBottom>
            No orders yet
          </Typography>
          <Typography color="text.secondary" paragraph sx={{ mb: 3 }}>
            Your order history will appear here
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => navigate('/')}
            size="large"
          >
            Start Shopping
          </Button>
        </Box>
      ) : (
        <>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {orders.map((order) => (
              <Grid item xs={12} key={order.id}>
                <Card elevation={2}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Box>
                        <Typography variant="h6" fontWeight="bold">
                          Order #{order.id}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(order.order_date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })}
                        </Typography>
                        <Chip 
                          icon={getStatusIcon(order.status)}
                          label={order.status}
                          color={getStatusColor(order.status)}
                          size="small"
                          sx={{ mt: 1, textTransform: 'capitalize' }}
                        />
                      </Box>
                      <Typography variant="h6" fontWeight="bold">
                        {currency}{(Number(order.total_amount) || 0).toFixed(2)}
                      </Typography>
                    </Box>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Box display="flex" flexDirection="column" gap={1}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                          {order.items?.length || 0} item{order.items?.length !== 1 ? 's' : ''}
                        </Typography>
                        <Button 
                          variant="outlined"
                          component={Link}
                          to={`/orders/${order.id}`}
                          size="small"
                        >
                          View Details
                        </Button>
                      </Box>
                      {order.items?.slice(0, 2).map((item, index) => (
                        <Box key={`${item.id}-${index}`} display="flex" alignItems="center" gap={1}>
                          <Avatar 
                            src={item.product_images?.[0]} 
                            alt={item.product_name}
                            variant="rounded"
                            sx={{ 
                              width: 32, 
                              height: 32,
                              bgcolor: 'background.paper',
                              '& img': {
                                objectFit: 'cover',
                                width: '100%',
                                height: '100%'
                              }
                            }}
                            imgProps={{
                              loading: 'lazy'
                            }}
                          >
                            {!item.product_images?.[0] && (
                              <ImageIcon sx={{ color: 'text.secondary' }} />
                            )}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography 
                              variant="body2" 
                              noWrap 
                              sx={{ 
                                maxWidth: '150px',
                                fontWeight: 'medium'
                              }}
                            >
                              {item.product_name}
                            </Typography>
                            <Box display="flex" gap={1} alignItems="center">
                              {item.size && (
                                <Chip 
                                  label={item.size} 
                                  size="small" 
                                  sx={{ 
                                    height: 20, 
                                    fontSize: '0.65rem',
                                    bgcolor: 'action.selected',
                                    color: 'text.secondary'
                                  }} 
                                />
                              )}
                              <Typography variant="body2" color="text.secondary">
                                ×{item.quantity}
                              </Typography>
                              <Typography variant="body2" color="text.primary" sx={{ ml: 'auto' }}>
                                ₹{item.price}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      ))}
                      {order.items?.length > 2 && (
                        <Typography variant="body2" color="text.secondary">
                          +{order.items.length - 2} more items...
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default Orders;
